import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import oddsDataService from '../../utils/services/oddsDataService';
import resultsTrackerService from '../../utils/services/resultsTrackerService';
import { useAuth } from '../../context/AuthContext';
import { useMultiBuilder } from '../../context/MultiBuilderContext';
import MultiBuilderTable from './MultiBuilderTable';
import MultiBuilderPanel from './MultiBuilderPanel';
import AddToTrackerMultiModal from './AddToTrackerMultiModal';
import CommunityTipModal from '../common/CommunityTipModal';
import Toast from '../Toast/Toast';
import { MULTI_BUILDER_DEFAULT_FILTERS } from './MultiBuilderFiltersModal';
import {
    BookmakerLogo,
    BOOKMAKER_DISPLAY_NAMES,
    BOOKMAKER_LOGOS,
    KNOWN_BOOKMAKERS,
    computeMultiMetrics,
    getImpliedFields,
    getLegKey,
    getAflPropTypeFromMarket,
} from './multiBuilderHelpers';

const REFRESH_INTERVAL = 10; // seconds

/**
 * Top-level Multi Builder.
 *
 * Layout: 70/30 split on desktop (table on the left, builder ticket on the right),
 * stacks single-column on mobile.
 *
 * State model:
 *  - `bets`              : the latest payload from `oddsDataService` (afl + market_and_implied_ev).
 *  - `selectedLegs`      : the user's current multi — one entry per leg with stable `key` and
 *                          its `anchorBookmaker` (the book the leg was added at). Persists across
 *                          refreshes; the leg's offered odds + market reference are re-synced
 *                          from the latest data whenever new bets arrive.
 *  - `bookmakerFilter`   : "all" or a specific bookmaker. When set, the table only shows legs
 *                          where that bookmaker is the best book. Auto-snaps to the first leg's
 *                          best book; manual changes that conflict with existing legs clear them.
 *  - `aflSeasonView`     : "2026" or "combined" — controls which historical dataset the table
 *                          and panel use for implied / EV calculations.
 */
function MultiBuilder() {
    const { hasRole } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const canTip = hasRole('COMMUNITY_TIPPER');
    const canUsePropTool = true;

    // Deep-link a leg's player (and its prop type) into the AFL Prop Tool.
    // Mirrors the Snipe Feed behaviour: the current path is passed as `from`
    // so the tool's Back button returns here, the line is derived from the
    // market threshold (e.g. "8+ Marks" → 7.5), and the implied-edge table
    // defaults to this leg's best-priced bookmaker.
    const openPropTool = useCallback((bet) => {
        if (!bet) return;
        const propType = getAflPropTypeFromMarket(bet.market);
        const thrMatch = /(\d+(?:\.\d+)?)/.exec(bet.market || '');
        const line = thrMatch ? Number(thrMatch[1]) - 0.5 : null;
        const bookmaker = (bet.valueOpportunities?.[0]?.bookmaker || '').toLowerCase() || null;
        navigate('/prop-tool', {
            state: { playerName: bet.player, propType, line, bookmaker, from: `${location.pathname}${location.search}` },
        });
    }, [navigate, location.pathname, location.search]);

    // Slip state (selected legs + locked bookmaker filter) is held in a global
    // context so it survives navigating away (e.g. to the Prop Tool) and back.
    const { selectedLegs, setSelectedLegs, bookmakerFilter, setBookmakerFilter } = useMultiBuilder();

    const [bets, setBets] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
    const [aflSeasonView, setAflSeasonView] = useState('2026');
    const [filters, setFilters] = useState(MULTI_BUILDER_DEFAULT_FILTERS);
    const [toast, setToast] = useState(null);
    const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
    const [trackerModalOpen, setTrackerModalOpen] = useState(false);
    const [isAddingBet, setIsAddingBet] = useState(false);
    const [unitSize, setUnitSize] = useState(20);
    const [tipModalOpen, setTipModalOpen] = useState(false);

    const countdownRef = useRef(null);
    const refreshRef = useRef(null);

    const showToast = useCallback((message, type = 'info') => {
        setToast({ message, type, id: Date.now() });
    }, []);

    // Pull the user's unit size from the results-tracker settings (same pattern
    // as Snipe Feed). Honours monthly-unit overrides if advanced settings are on.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const result = await resultsTrackerService.getSettings();
            if (cancelled || !result.success || !result.data) return;
            const { advancedUnitSettings, monthlyUnits, unitSize: defaultUnit } = result.data;
            if (advancedUnitSettings && monthlyUnits?.length > 0) {
                const now = new Date();
                const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                const match = monthlyUnits
                    .filter((m) => m.startDate?.substring(0, 7) <= currentMonth)
                    .sort((a, b) => b.startDate.localeCompare(a.startDate))[0];
                setUnitSize(match?.unitSize || defaultUnit || 20);
            } else {
                setUnitSize(defaultUnit || 20);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const handleAddToTracker = useCallback(async (betPayload) => {
        setIsAddingBet(true);
        const result = await resultsTrackerService.addBet(betPayload);
        if (result.success) {
            showToast('Multi added to tracker successfully', 'success');
            setTrackerModalOpen(false);
        } else if (result.isAuthError) {
            showToast('Session expired. Please log in again.', 'error');
        } else {
            showToast(result.message || 'Failed to add multi to tracker', 'error');
        }
        setIsAddingBet(false);
        return result;
    }, [showToast]);

    // The bookmaker every leg in the slip shares (null until the first leg is added).
    const multiBookmaker = selectedLegs[0]?.anchorBookmaker || null;

    /** Fetch latest AFL value bets. */
    const fetchData = useCallback(async (showLoading = false) => {
        if (showLoading) setIsLoading(true);
        setError(null);
        const result = await oddsDataService.getOddsData('afl', 'market_and_implied_ev');
        if (result.success) {
            const fresh = result.data?.bets || [];
            setBets(fresh);
            setLastUpdated(result.data?.lastUpdated || new Date().toISOString());
        } else {
            const err = {
                isSubscriptionError: result.isSubscriptionError,
                isAuthError: result.isAuthError,
                title: result.isSubscriptionError
                    ? 'Subscription required'
                    : result.isAuthError
                        ? 'Session expired'
                        : 'Unable to load value bets',
                message: result.message || 'Try again in a moment.',
            };
            setError(err);
            if (result.isAuthError) showToast('Session expired. Please log in again.', 'error');
        }
        if (showLoading) setIsLoading(false);
    }, [showToast]);

    // Re-sync selected legs whenever a new payload arrives. The leg's offered price tracks
    // the leg's own `anchorBookmaker`, not the (UI-level) `bookmakerFilter`.
    useEffect(() => {
        if (!Array.isArray(bets) || bets.length === 0) return;
        setSelectedLegs((prev) => {
            if (prev.length === 0) return prev;
            const byKey = new Map();
            bets.forEach((b) => byKey.set(getLegKey(b), b));
            let dirty = false;
            const next = prev.map((leg) => {
                const updated = byKey.get(leg.key);
                if (!updated) {
                    if (!leg.stale) {
                        dirty = true;
                        return { ...leg, stale: true };
                    }
                    return leg;
                }
                const bookmakerOdds = {};
                updated.bookmakerOdds?.forEach((bo) => {
                    if (bo?.bookmaker && bo?.odds != null) bookmakerOdds[bo.bookmaker] = Number(bo.odds);
                });
                const anchor = leg.anchorBookmaker;
                const anchorOdds = anchor ? bookmakerOdds[anchor] ?? null : null;
                const opp = updated.valueOpportunities?.[0] || {};
                const rawSeasonData = {
                    '2025_line_hit': opp['2025_line_hit'] ?? leg.rawSeasonData?.['2025_line_hit'] ?? null,
                    '2025_total_games': opp['2025_total_games'] ?? leg.rawSeasonData?.['2025_total_games'] ?? null,
                    '2025_hit_rate_percentage': opp['2025_hit_rate_percentage'] ?? leg.rawSeasonData?.['2025_hit_rate_percentage'] ?? null,
                    '2025_implied_odds': opp['2025_implied_odds'] ?? leg.rawSeasonData?.['2025_implied_odds'] ?? null,
                    '2025_implied_ev': opp['2025_implied_ev'] ?? leg.rawSeasonData?.['2025_implied_ev'] ?? null,
                    '2026_line_hit': opp['2026_line_hit'] ?? leg.rawSeasonData?.['2026_line_hit'] ?? null,
                    '2026_total_games': opp['2026_total_games'] ?? leg.rawSeasonData?.['2026_total_games'] ?? null,
                    '2026_hit_rate_percentage': opp['2026_hit_rate_percentage'] ?? leg.rawSeasonData?.['2026_hit_rate_percentage'] ?? null,
                    '2026_implied_odds': opp['2026_implied_odds'] ?? leg.rawSeasonData?.['2026_implied_odds'] ?? null,
                    '2026_implied_ev': opp['2026_implied_ev'] ?? leg.rawSeasonData?.['2026_implied_ev'] ?? null,
                };
                const refreshed = {
                    ...leg,
                    stale: false,
                    player: updated.player,
                    market: updated.market,
                    match: updated.match,
                    startTime: updated.startTime,
                    offeredOdds: anchorOdds != null ? anchorOdds : leg.offeredOdds,
                    avgMarketOdds: Number(opp.avgMarketOdds ?? leg.avgMarketOdds),
                    impliedEdgePercentage: Number(opp.impliedEdgePercentage ?? leg.impliedEdgePercentage),
                    rawSeasonData,
                    bookmakerOdds,
                };
                if (
                    refreshed.offeredOdds !== leg.offeredOdds ||
                    refreshed.avgMarketOdds !== leg.avgMarketOdds ||
                    refreshed.impliedEdgePercentage !== leg.impliedEdgePercentage
                ) {
                    dirty = true;
                }
                return refreshed;
            });
            return dirty ? next : prev;
        });
    }, [bets]);

    // Refresh + countdown loop (mirrors Snipe Feed's 10s cadence).
    useEffect(() => {
        fetchData(true);
        setCountdown(REFRESH_INTERVAL);
        if (countdownRef.current) clearInterval(countdownRef.current);
        if (refreshRef.current) clearInterval(refreshRef.current);
        countdownRef.current = setInterval(() => {
            setCountdown((prev) => (prev <= 1 ? REFRESH_INTERVAL : prev - 1));
        }, 1000);
        refreshRef.current = setInterval(() => {
            fetchData(false);
        }, REFRESH_INTERVAL * 1000);
        return () => {
            if (countdownRef.current) clearInterval(countdownRef.current);
            if (refreshRef.current) clearInterval(refreshRef.current);
        };
    }, [fetchData]);

    // For every leg key, what each bookmaker is currently offering. Used by the panel.
    const bookmakerOddsByLeg = useMemo(() => {
        const map = {};
        bets.forEach((b) => {
            const key = getLegKey(b);
            const inner = {};
            b.bookmakerOdds?.forEach((bo) => {
                if (bo?.bookmaker && bo?.odds != null) inner[bo.bookmaker] = Number(bo.odds);
            });
            map[key] = inner;
        });
        selectedLegs.forEach((leg) => {
            if (!map[leg.key]) map[leg.key] = { ...(leg.bookmakerOdds || {}) };
        });
        return map;
    }, [bets, selectedLegs]);

    // Bookmakers currently offering the best price on at least one leg. Drives the chip strip.
    const availableBestBooks = useMemo(() => {
        if (!Array.isArray(bets)) return [];
        const set = new Set();
        bets.forEach((b) => {
            const bk = b?.valueOpportunities?.[0]?.bookmaker;
            if (bk) set.add(bk);
        });
        const arr = Array.from(set);
        const knownIdx = (bk) => {
            const i = KNOWN_BOOKMAKERS.indexOf(bk);
            return i === -1 ? Number.MAX_SAFE_INTEGER : i;
        };
        arr.sort((a, b) => {
            const ai = knownIdx(a);
            const bi = knownIdx(b);
            if (ai !== bi) return ai - bi;
            return a.localeCompare(b);
        });
        return arr;
    }, [bets]);

    const handleToggleLeg = useCallback((bet) => {
        const key = getLegKey(bet);
        setSelectedLegs((prev) => {
            const existing = prev.find((l) => l.key === key);
            if (existing) {
                const next = prev.filter((l) => l.key !== key);
                // Unchecking the last leg releases the bookmaker filter back to All.
                if (next.length === 0) setBookmakerFilter('all');
                return next;
            }

            const opp = bet?.valueOpportunities?.[0];
            const bookmakerOdds = {};
            bet?.bookmakerOdds?.forEach((bo) => {
                if (bo?.bookmaker && bo?.odds != null) bookmakerOdds[bo.bookmaker] = Number(bo.odds);
            });

            const legAnchor = opp?.bookmaker || Object.entries(bookmakerOdds).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
            if (!legAnchor) {
                showToast(`${bet.player} — no priced bookmaker available.`, 'warning');
                return prev;
            }

            // All legs in a multi must share the same anchor bookmaker (single ticket).
            if (multiBookmaker && legAnchor !== multiBookmaker) {
                showToast(
                    `${bet.player} — best price is at ${BOOKMAKER_DISPLAY_NAMES[legAnchor] || legAnchor}; multi is locked to ${BOOKMAKER_DISPLAY_NAMES[multiBookmaker] || multiBookmaker}.`,
                    'warning'
                );
                return prev;
            }

            const offered = bookmakerOdds[legAnchor] ?? Number(opp?.odds ?? 0);
            const rawSeasonData = {
                '2025_line_hit': opp?.['2025_line_hit'] ?? null,
                '2025_total_games': opp?.['2025_total_games'] ?? null,
                '2025_hit_rate_percentage': opp?.['2025_hit_rate_percentage'] ?? null,
                '2025_implied_odds': opp?.['2025_implied_odds'] ?? null,
                '2025_implied_ev': opp?.['2025_implied_ev'] ?? null,
                '2026_line_hit': opp?.['2026_line_hit'] ?? null,
                '2026_total_games': opp?.['2026_total_games'] ?? null,
                '2026_hit_rate_percentage': opp?.['2026_hit_rate_percentage'] ?? null,
                '2026_implied_odds': opp?.['2026_implied_odds'] ?? null,
                '2026_implied_ev': opp?.['2026_implied_ev'] ?? null,
            };

            const leg = {
                key,
                player: bet.player,
                match: bet.match,
                market: bet.market,
                startTime: bet.startTime,
                offeredOdds: Number(offered),
                avgMarketOdds: Number(opp?.avgMarketOdds ?? 0),
                impliedEdgePercentage: Number(opp?.impliedEdgePercentage ?? 0),
                rawSeasonData,
                bookmakerOdds,
                anchorBookmaker: legAnchor,
                stale: false,
            };

            // First leg snaps the (UI) filter to that leg's best book.
            if (prev.length === 0) {
                setBookmakerFilter(legAnchor);
            }

            return [...prev, leg];
        });
    }, [multiBookmaker, showToast]);

    const handleBookmakerFilterChange = useCallback((next) => {
        setBookmakerFilter((current) => {
            if (next === current) return current;
            // Any filter change away from the multi's current anchor bookmaker
            // clears the slip — including switching back to "all books". Keeping
            // legs around when the user expands the filter would leave them
            // anchored to a single book without any visible cue as to why.
            if (multiBookmaker && next !== multiBookmaker) {
                setSelectedLegs([]);
                const label = next === 'all' ? 'all bookmakers' : (BOOKMAKER_DISPLAY_NAMES[next] || next);
                showToast(`Multi cleared — switched to ${label}.`, 'info');
            }
            return next;
        });
    }, [multiBookmaker, showToast]);

    const handleRemoveLeg = useCallback((leg) => {
        setSelectedLegs((prev) => {
            const next = prev.filter((l) => l.key !== leg.key);
            if (next.length === 0) setBookmakerFilter('all');
            return next;
        });
    }, []);

    const handleClearAll = useCallback(() => {
        setSelectedLegs([]);
        setBookmakerFilter('all');
    }, []);

    // Dataset-adjusted leg snapshots for the panel (implied fields swap with seasonView).
    const legsForPanel = useMemo(() => selectedLegs.map((leg) => {
        const fields = getImpliedFields(leg.rawSeasonData, aflSeasonView, leg.offeredOdds);
        return {
            ...leg,
            hitRatePercentage: fields.hitRatePercentage,
            impliedOdds: fields.impliedOdds,
            impliedEv: fields.impliedEv,
        };
    }), [selectedLegs, aflSeasonView]);

    // Canonical multi metrics — single source of truth shared by the mobile
    // dock, the Add to Tracker modal, and the community-tip modal. Re-deriving
    // these inside individual modals causes tiny precision/rounding drifts
    // (e.g. EV% off by ~0.1pp because the modal used unrounded combinedOdds).
    const mobileDockMetrics = useMemo(() => computeMultiMetrics(legsForPanel), [legsForPanel]);
    const hasSameGameLegs = useMemo(() => {
        if (selectedLegs.length < 2) return false;
        const seen = new Set();
        for (const leg of selectedLegs) {
            const m = leg.match || '';
            if (!m) continue;
            if (seen.has(m)) return true;
            seen.add(m);
        }
        return false;
    }, [selectedLegs]);

    // Auto-close the mobile sheet once the slip empties; lock body scroll while open.
    useEffect(() => {
        if (selectedLegs.length === 0 && mobileSheetOpen) {
            setMobileSheetOpen(false);
        }
    }, [selectedLegs.length, mobileSheetOpen]);
    useEffect(() => {
        if (!mobileSheetOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e) => { if (e.key === 'Escape') setMobileSheetOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener('keydown', onKey);
        };
    }, [mobileSheetOpen]);

    return (
        <div className="flex flex-col p-2 md:p-4 h-[calc(100dvh-72px)] md:h-[100dvh] overflow-hidden">
            {/* Bookmaker filter chip strip — wraps on desktop, single-line horizontal
                scroll on mobile to reclaim vertical real estate for the card list. */}
            <div
                className="mb-2 md:mb-3 flex items-center gap-1.5 md:gap-2 flex-nowrap md:flex-wrap overflow-x-auto md:overflow-visible flex-shrink-0 -mx-2 px-2 md:mx-0 md:px-0"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                <span className="text-[9px] font-mono text-slate-600 uppercase tracking-widest mr-1 hidden md:inline">
                    Filter best book
                </span>
                <BookmakerChip
                    active={bookmakerFilter === 'all'}
                    onClick={() => handleBookmakerFilterChange('all')}
                    label="All books"
                />
                {availableBestBooks.map((bk) => (
                    <BookmakerChip
                        key={bk}
                        active={bookmakerFilter === bk}
                        onClick={() => handleBookmakerFilterChange(bk)}
                        bookmaker={bk}
                        label={BOOKMAKER_DISPLAY_NAMES[bk] || bk}
                    />
                ))}
            </div>

            <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-2 md:gap-3 overflow-hidden">
                {/* Table — 75% on desktop, full width on mobile */}
                <div className="flex-1 min-h-0 lg:basis-[75%] lg:max-w-[75%] flex flex-col overflow-hidden">
                    <MultiBuilderTable
                        bets={bets}
                        isLoading={isLoading}
                        error={error}
                        lastUpdated={lastUpdated}
                        countdown={countdown}
                        onRefresh={() => fetchData(true)}
                        aflSeasonView={aflSeasonView}
                        setAflSeasonView={setAflSeasonView}
                        selectedLegs={selectedLegs}
                        bookmakerFilter={bookmakerFilter}
                        onToggleLeg={handleToggleLeg}
                        filters={filters}
                        onApplyFilters={setFilters}
                        bookmakerLogos={BOOKMAKER_LOGOS}
                        bookmakerNames={BOOKMAKER_DISPLAY_NAMES}
                        canUsePropTool={canUsePropTool}
                        onOpenPropTool={openPropTool}
                    />
                </div>

                {/* Panel — desktop only (25% column). Mobile uses the dock + sheet below. */}
                <div className="hidden lg:flex flex-shrink-0 lg:basis-[25%] lg:max-w-[25%] flex-col overflow-hidden min-h-0">
                    <MultiBuilderPanel
                        legs={legsForPanel}
                        multiBookmaker={multiBookmaker}
                        aflSeasonView={aflSeasonView}
                        bookmakerOddsByLeg={bookmakerOddsByLeg}
                        onRemoveLeg={handleRemoveLeg}
                        onClearAll={handleClearAll}
                        onAddToTracker={() => setTrackerModalOpen(true)}
                        onSubmitTip={canTip ? () => setTipModalOpen(true) : null}
                    />
                </div>
            </div>

            {/* Mobile bet-slip dock — only when legs > 0. Lives in document flow
                below the table so the table reserves space and the dock never
                covers content. */}
            {selectedLegs.length > 0 && (
                <MobileBetSlipDock
                    legCount={selectedLegs.length}
                    combinedOdds={mobileDockMetrics.combinedOdds}
                    multiBookmaker={multiBookmaker}
                    hasSameGameLegs={hasSameGameLegs}
                    onOpen={() => setMobileSheetOpen(true)}
                    onClear={handleClearAll}
                />
            )}

            {/* Mobile bet-slip sheet — portaled to body for clean z-index. */}
            {mobileSheetOpen && (
                <MobileBetSlipSheet onClose={() => setMobileSheetOpen(false)}>
                    <MultiBuilderPanel
                        legs={legsForPanel}
                        multiBookmaker={multiBookmaker}
                        aflSeasonView={aflSeasonView}
                        bookmakerOddsByLeg={bookmakerOddsByLeg}
                        onRemoveLeg={handleRemoveLeg}
                        onClearAll={handleClearAll}
                        onAddToTracker={() => setTrackerModalOpen(true)}
                        onSubmitTip={canTip ? () => setTipModalOpen(true) : null}
                        chromeless
                    />
                </MobileBetSlipSheet>
            )}

            {/* Add to Tracker — saves the multi as a single tracker row with
                `game = 'MULTI'` and `bet = leg descriptions joined by commas`. */}
            {trackerModalOpen && selectedLegs.length >= 2 && (
                <AddToTrackerMultiModal
                    legs={legsForPanel}
                    multiBookmaker={multiBookmaker}
                    bookmakerLogos={BOOKMAKER_LOGOS}
                    aflSeasonView={aflSeasonView}
                    hasSameGameLegs={hasSameGameLegs}
                    unitSize={unitSize}
                    sport="AFL"
                    onClose={() => setTrackerModalOpen(false)}
                    onAddToTracker={handleAddToTracker}
                    isAddingBet={isAddingBet}
                />
            )}

            {/* Community Tip (MULTI_BUILDER) — only mounted for COMMUNITY_TIPPER
                users. Dataset is mapped from the in-app season toggle (`2026`
                or `combined`) to the API enum (`2026` or `2025_2026`). Metrics
                are passed in pre-computed (via `computeMultiMetrics`) so the
                modal can never drift from what the panel + tracker show. */}
            {canTip && (
                <CommunityTipModal
                    open={tipModalOpen && selectedLegs.length >= 2}
                    tool="MULTI_BUILDER"
                    sport="afl"
                    multiLegs={legsForPanel}
                    multiBookmaker={multiBookmaker}
                    multiMetrics={mobileDockMetrics}
                    multiDataset={aflSeasonView === 'combined' ? '2025_2026' : '2026'}
                    multiDataSource="SNIPE_FEED"
                    hasSameGameLegs={hasSameGameLegs}
                    bookmakerLogos={BOOKMAKER_LOGOS}
                    onClose={() => setTipModalOpen(false)}
                    onSuccess={() => showToast('Multi tip submitted to community feed', 'success')}
                    onAuthError={() => showToast('Session expired. Please log in again.', 'error')}
                />
            )}

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    duration={3000}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
}

function BookmakerChip({ active, onClick, bookmaker, label }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            title={label}
            className={`inline-flex flex-shrink-0 items-center gap-1.5 px-2 py-1 rounded-md border text-[10px] font-mono uppercase tracking-widest transition-colors ${
                active
                    ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-200'
                    : 'bg-slate-900/60 border-slate-700/50 text-slate-400 hover:border-slate-600 hover:text-slate-200'
            }`}
        >
            {bookmaker && <BookmakerLogo bookmaker={bookmaker} size={14} />}
            <span>{label}</span>
        </button>
    );
}

/**
 * Mobile bet-slip dock — a sticky summary bar that appears at the bottom of the
 * Multi Builder on mobile when at least one leg is selected. Visual language is
 * the ticker / bet-ticket aesthetic established by the desktop panel:
 *   - decorative perforation dots on the outer edges
 *   - emerald hairline at the top
 *   - thin amber accent + label when the slip contains same-game legs
 *
 * Tapping anywhere on the dock opens the full panel as a bottom sheet.
 */
function MobileBetSlipDock({
    legCount,
    combinedOdds,
    multiBookmaker,
    hasSameGameLegs,
    onOpen,
    onClear,
}) {
    return (
        <div
            className="lg:hidden flex-shrink-0 mt-2"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 0px)' }}
        >
            <button
                type="button"
                onClick={onOpen}
                aria-label="Open bet slip"
                className={`relative w-full text-left rounded-xl border bg-gradient-to-br from-slate-900 to-slate-950 px-3 py-2.5 transition-all overflow-hidden ${
                    hasSameGameLegs
                        ? 'border-amber-500/40 shadow-[0_-10px_30px_-12px_rgba(245,158,11,0.25)]'
                        : 'border-emerald-500/40 shadow-[0_-10px_30px_-12px_rgba(16,185,129,0.35)]'
                }`}
            >
                {/* Emerald/amber hairline at the very top */}
                <span
                    aria-hidden="true"
                    className={`absolute top-0 left-6 right-6 h-px ${
                        hasSameGameLegs
                            ? 'bg-gradient-to-r from-transparent via-amber-400/70 to-transparent'
                            : 'bg-gradient-to-r from-transparent via-emerald-400/80 to-transparent'
                    }`}
                />
                {/* Perforations to match the desktop panel banner */}
                <span aria-hidden="true" className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#020817] border border-slate-700/40" />
                <span aria-hidden="true" className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#020817] border border-slate-700/40" />

                <div className="relative flex items-center gap-3">
                    {/* Combined odds — the headline number */}
                    <div className="flex-shrink-0">
                        <div className="text-[9px] font-mono text-slate-600 uppercase tracking-[0.18em] leading-none">
                            {hasSameGameLegs ? 'Non-corr. odds' : 'Combined'}
                        </div>
                        <div className={`text-xl font-mono font-bold tabular-nums leading-none mt-1 ${
                            hasSameGameLegs ? 'text-amber-200' : 'text-white'
                        }`}>
                            {combinedOdds != null ? combinedOdds.toFixed(2) : '—'}
                        </div>
                    </div>

                    {/* Vertical divider */}
                    <span aria-hidden="true" className="self-stretch w-px bg-slate-700/40" />

                    {/* Legs + bookmaker */}
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest leading-none">
                            {legCount} leg{legCount === 1 ? '' : 's'}
                        </div>
                        {multiBookmaker && (
                            <div className="flex items-center gap-1.5 mt-1.5 min-w-0">
                                <BookmakerLogo bookmaker={multiBookmaker} size={12} />
                                <span className="text-[11px] font-mono text-slate-300 truncate">
                                    {BOOKMAKER_DISPLAY_NAMES[multiBookmaker] || multiBookmaker}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Clear */}
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onClear(); }}
                        aria-label="Clear all legs"
                        className="flex-shrink-0 w-7 h-7 rounded-md inline-flex items-center justify-center text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Open arrow / CTA */}
                    <span
                        aria-hidden="true"
                        className={`flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg ${
                            hasSameGameLegs
                                ? 'bg-amber-400/15 border border-amber-400/40 text-amber-300'
                                : 'bg-emerald-400/15 border border-emerald-400/40 text-emerald-300'
                        }`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                        </svg>
                    </span>
                </div>
            </button>
        </div>
    );
}

/**
 * Mobile bet-slip sheet — portaled, full-width bottom-sheet that wraps the
 * existing `MultiBuilderPanel`. Backdrop tap and escape both close.
 */
function MobileBetSlipSheet({ onClose, children }) {
    return createPortal(
        <div
            className="lg:hidden fixed inset-0 flex items-end justify-center bg-slate-950/80 backdrop-blur-sm animate-fade-in"
            style={{ zIndex: 9999 }}
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="Bet slip"
        >
            <div
                className="relative w-full max-h-[90vh] bg-slate-900 border-t border-slate-800 rounded-t-2xl shadow-2xl shadow-black/60 overflow-hidden flex flex-col animate-slide-up"
                onClick={(e) => e.stopPropagation()}
                style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 0px)' }}
            >
                {/* Emerald top hairline + glow */}
                <span aria-hidden="true" className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-400/70 to-transparent" />
                <div aria-hidden="true" className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-20 bg-emerald-500/10 blur-3xl pointer-events-none" />

                {/* Grab handle */}
                <div className="flex justify-center pt-2 pb-1 flex-shrink-0">
                    <span aria-hidden="true" className="block w-10 h-1 rounded-full bg-slate-700/80" />
                </div>

                {/* Close button — overlays the panel's own header */}
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close bet slip"
                    className="absolute top-2 right-3 z-10 w-8 h-8 rounded-md inline-flex items-center justify-center text-slate-500 hover:text-white hover:bg-slate-800/70 transition-colors"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
                    </svg>
                </button>

                {/* Panel content — reuses the desktop panel unchanged */}
                <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                    {children}
                </div>
            </div>
        </div>,
        document.body
    );
}

export default MultiBuilder;
